import { FAQs } from './Faqs';
import { Reviews } from './Reviews';
import { TextSection2 } from './TextSection2';
import { Testimonials } from './Testimonials';
import { IntroductionForm } from './Introduction';
import { TextSection1 } from './TextSection1';
import { BioSection } from './BioSection';
import { WorkoutsSelect } from './WorkoutSection';
import { OfferDetailsSection } from './OfferDetailsSection';
import { OTPFeatures } from './OTPFeaturesSection';
import { UseFormRegisterReturn } from 'react-hook-form';
import { ProgramStats } from './ProgramStats';
import { EmailCaptureForm } from '@client/new/pages/LandingPagesBuilder/EmailCapture';
import { FeaturesNewSection } from '@client/new/pages/LandingPagesBuilder/FeaturesNewSection';
import { IFeaturesNewSection } from '@playbook-technologies/common-schemas';
import { ErrorMessage } from '@client/new/components/Label';
import { GetProgramPurchasePageByIdQuery } from 'generated/graphql';
import { Col } from '@design-system/components/grid/grid';
import {
  disabledSectionTypePostfix,
  SectionSchema,
} from './OTPLandingPage.schema';
import { OfferDetailsData } from './OfferDetails.mappers';

type RawProgram = GetProgramPurchasePageByIdQuery['program'];

export const SectionRenderer = <T extends SectionSchema>({
  program,
  influencerId,
  onUpload,
  primaryColor,
  value,
  offerData,
  onWorkoutClick,
  ...props
}: {
  onUpload: (file: File) => Promise<string>;
  program: RawProgram;
  influencerId: number;
  primaryColor: string;
  register: (s: string) => UseFormRegisterReturn;
  onChange: (value: SectionSchema) => void;
  value: T;
  index: number;
  offerData?: OfferDetailsData;
  onWorkoutClick?: (workoutId: number, programId?: number | null) => void;
}): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any

  const nextValue: T = {
    ...value,
    type: value.type.replace(disabledSectionTypePostfix, '') as any,
  };
  try {
    switch (nextValue.type) {
      case 'EmailCapture':
        return <EmailCaptureForm {...props} imageUpload={onUpload} />;
      case 'Introduction':
        return (
          <IntroductionForm
            {...props}
            imageUpload={onUpload}
            videoUpload={onUpload}
            value={nextValue}
            offerData={offerData}
          />
        );

      case 'OTPFeatures':
      case 'Features':
        return (
          <OTPFeatures {...props} value={nextValue} imageUpload={onUpload} />
        );

      case 'FeaturesNew':
        return (
          <FeaturesNewSection
            value={nextValue as unknown as IFeaturesNewSection}
            onChange={props.onChange as (x: IFeaturesNewSection) => void}
            imageUpload={onUpload}
          />
        );

      case 'TextSection1':
        return (
          <TextSection1
            {...props}
            imageUpload={onUpload}
            videoUpload={onUpload}
          />
        );

      case 'Workouts':
        return (
          <WorkoutsSelect
            {...props}
            workouts={program?.workouts}
            influencerId={influencerId}
          />
        );
      case 'OfferDetails':
        return (
          <OfferDetailsSection
            {...props}
            offerData={offerData}
            onWorkoutClick={onWorkoutClick}
          />
        );
      case 'Reviews':
        return <Reviews {...props} value={nextValue} />;

      case 'Testimonials':
        return <Testimonials {...props} upload={onUpload} value={nextValue} />;
      case 'Bio':
        return <BioSection {...props} imageUpload={onUpload} />;
      case 'TextSection2':
        return <TextSection2 {...props} upload={onUpload} />;
      case 'FAQs':
        return <FAQs {...props} value={nextValue} />;
      case 'ProgramStats':
        // ProgramStats requires a program, not available in offer mode
        if (!program) {
          return (
            <Col gap="m">
              <ErrorMessage
                size="large"
                content="ProgramStats section is not available for offer landing pages. Please remove this section."
              />
            </Col>
          );
        }
        return (
          <ProgramStats
            {...props}
            program={program}
            primaryColor={primaryColor}
          />
        );

      default:
        throw new Error('Unsupported section ' + JSON.stringify(nextValue));
    }
  } catch (err) {
    return (
      <Col gap="m">
        <div>
          <ErrorMessage
            size="large"
            content={`Cannot render section of type: ${nextValue.type}`}
          />
        </div>
        <div>
          <ErrorMessage
            size="large"
            content={
              <>
                Please take a screenshot and contact Contact our{' '}
                <a
                  href="mailto:creatorsupport@playbookapp.io"
                  className="underline text-inherit"
                >
                  support team
                </a>
              </>
            }
          />
        </div>

        <div>
          <p className="p-4">{`${err?.message}`}</p>
        </div>
      </Col>
    );
  }
};
